import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'

import translations from '@translationKeys'

import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { FetchEventsResponse, POneEvent } from '@modules/hub/types'
import { useMediaQuery, useTheme } from '@mui/material'
import { PONE_PLUS_WEBSITE_URL } from '@shared/constants'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useQuery } from '@tanstack/react-query'

import hubApi from '../../api'
import partneringOneLogoImage from '../../assets/partneringONELogo@2x.png'
import HubIntroSection from '../HubIntroSection'
import { IntroButton, StyledNavigateNextIcon } from '../HubIntroSection/index.styles'
import {
  CurrentEventsContainer,
  EventPaginationBlock,
  EventPaginationDot,
  EventTile,
  EventTileEventPartneringLabel,
  EventTileGoToLabel,
  EventTileLabelArrow,
  EventTileLabelContainer,
  EventTileLabelText,
  EventsColumnSeparator,
  EventsContainer,
  EventsContainerPart,
  EventsContainerWrapper,
  EventsLabel,
  Header,
  LogoImage,
  LogoImageWrapper,
  Root,
  StyledContainer,
  StyledUserProfileSection,
} from './index.styles'

export const DEFAULT_EVENT_PAGE_LIMIT = 5
const DEFAULT_CURRENT_EVENTS_STEP = 2

interface TopSectionProps {
  setPastEventsCarouselShown: (value: boolean) => void
  pastEvents: POneEvent[]
  fetchNextPastEventsPage: () => void
}

const TopSection = ({ setPastEventsCarouselShown, pastEvents, fetchNextPastEventsPage }: TopSectionProps) => {
  const { t } = useTranslation()

  const { user } = useAuthContext()

  const { hasAccessToSearch } = useFeatureFlags()
  const navigate = useNavigate()

  const { inView: isPastEventsAnchorInView } = useInView({ threshold: 0.1 })
  const [upcomingEventsPage, setUpcomingEventsPage] = useState(0)

  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getEventErrorMessage = (event: POneEvent): string | undefined => {
    if (!event) {
      return undefined
    }

    if (!event.delegateEventProfileId) {
      return t(translations.SORRY_YOUR_ACCOUNT_FOR_THIS_EVENT_IS_NOT_READY_YET) as string
    }

    if (event.isDelegateBlocked) {
      return t(translations.SORRY_YOUR_ACCOUNT_FOR_THIS_EVENT_IS_BLOCKED) as string
    }

    const loginArea = event.userAreas.find(area => area.id === 2)
    const isLoginBlocked = !loginArea || !loginArea.checked

    if (isLoginBlocked) {
      return t(translations.SORRY_ACCESS_ISNT_OPEN_FOR_THIS_EVENT_JUST_YET) as string
    }

    const calendarArea = event.userAreas.find(area => area.id === 4)
    const isCalendarBlocked = !calendarArea || !calendarArea.checked

    const requestingArea = event.userAreas.find(area => area.id === 7)
    const isRequestingBlocked = !requestingArea || !requestingArea.checked

    if (isCalendarBlocked && isRequestingBlocked && isMobile) {
      return t(translations.SORRY_AGENDA_ISNT_OPEN_FOR_THIS_EVENT_JUST_YET) as string
    }

    return undefined
  }

  const { data: currentUpcomingEventsData } = useQuery<FetchEventsResponse>({
    queryKey: [hubApi.fetchEvents.key, user?.id, 'current_upcoming'],
    queryFn: () =>
      hubApi.fetchEvents.fn({
        personId: user?.id as number,
        type: 'current_upcoming',
        page: 1,
      }),
    refetchOnMount: 'always',
  })

  const preparedCurrentUpcomingEvents = useMemo(() => {
    if (!currentUpcomingEventsData?.rows?.length) {
      return pastEvents?.length ? [{ ...pastEvents[0], isPast: true, error: getEventErrorMessage(pastEvents[0]) }] : []
    }

    return [...currentUpcomingEventsData.rows]
      .sort((event1, event2) => new Date(event1.endDate).getTime() - new Date(event2.endDate).getTime())
      .map(currentUpcomingEvent => ({
        ...currentUpcomingEvent,
        isPast: false,
        error: getEventErrorMessage(currentUpcomingEvent),
      }))
  }, [currentUpcomingEventsData?.rows, pastEvents])

  const currentUpcomingEvents = useMemo(() => {
    const offset = upcomingEventsPage * DEFAULT_CURRENT_EVENTS_STEP
    return preparedCurrentUpcomingEvents.slice(offset, offset + DEFAULT_CURRENT_EVENTS_STEP)
  }, [preparedCurrentUpcomingEvents, upcomingEventsPage])

  const currentUpcomingEventsPages = useMemo(
    () => Array.from(Array(Math.ceil(preparedCurrentUpcomingEvents.length / 2)).keys()),
    [currentUpcomingEventsData?.count],
  )

  useEffect(() => {
    if (isPastEventsAnchorInView) {
      fetchNextPastEventsPage()
    }
  }, [isPastEventsAnchorInView])

  return (
    <Root>
      <StyledContainer fixed>
        <Header>
          <LogoImageWrapper>
            <LogoImage alt="" src={partneringOneLogoImage} />
          </LogoImageWrapper>
          {user && <StyledUserProfileSection user={user} hidePendoGuideIcon />}
        </Header>
        <EventsContainerWrapper>
          <EventsContainer>
            <EventsContainerPart>
              {!!currentUpcomingEvents.length && (
                <>
                  <EventsLabel>{t(translations.EVENTS)}</EventsLabel>
                  <CurrentEventsContainer>
                    {currentUpcomingEvents.map(event => (
                      <EventTile
                        bannerUrl={event?.banner}
                        href={event && !event.error ? `/event/${event?.id}` : undefined}
                        name={event?.name}
                        errorMessage={event.error}
                        overlay={
                          <EventTileLabelContainer>
                            <EventTileLabelText>
                              <EventTileGoToLabel>{t(translations.GO_TO)}</EventTileGoToLabel>{' '}
                              <EventTileEventPartneringLabel>
                                {!event.isPast ? t(translations.UPCOMING_EVENT) : t(translations.PAST_EVENT)}
                              </EventTileEventPartneringLabel>
                            </EventTileLabelText>
                            <EventTileLabelArrow />
                          </EventTileLabelContainer>
                        }
                      />
                    ))}
                  </CurrentEventsContainer>
                  {currentUpcomingEventsPages.length > 1 && (
                    <EventPaginationBlock>
                      {currentUpcomingEventsPages.map(item => (
                        <EventPaginationDot onClick={() => setUpcomingEventsPage(item)} $isActive={upcomingEventsPage === item} />
                      ))}
                    </EventPaginationBlock>
                  )}
                </>
              )}
              {pastEvents?.length ? (
                <IntroButton
                  onClick={() => {
                    setPastEventsCarouselShown(true)
                  }}
                >
                  <EventTileLabelText>
                    <EventTileGoToLabel>
                      {currentUpcomingEventsData?.rows?.length ? t(translations.SEE_YOUR) : t(translations.SEE_ALL)}
                    </EventTileGoToLabel>{' '}
                    <EventTileEventPartneringLabel>{t(translations.PAST_EVENTS)}</EventTileEventPartneringLabel>
                  </EventTileLabelText>
                  <StyledNavigateNextIcon />
                </IntroButton>
              ) : null}
            </EventsContainerPart>

            {!!currentUpcomingEvents.length || pastEvents?.length ? <EventsColumnSeparator /> : null}

            {hasAccessToSearch ? (
              <HubIntroSection
                title={t(translations.FIND_CONNECT_AND_MANAGE)}
                buttonTitle={t(translations.LAUNCH_PARTNERING_ONE_PLUS)}
                onButtonClick={() => {
                  navigate('/plus/search')
                }}
              />
            ) : (
              <HubIntroSection
                title={t(translations.TO_FIND_CONNECT_AND_MANAGE)}
                buttonTitle={t(translations.FIND_OUT_MORE)}
                linkHref={PONE_PLUS_WEBSITE_URL}
              />
            )}
          </EventsContainer>
        </EventsContainerWrapper>
      </StyledContainer>
    </Root>
  )
}

export default TopSection
